import { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import TagItAiDemo from '../components/TagItAiDemo';
import ContactModal from '../components/ContactModal';
import heroBanner from '../../assets/tagIt-orchestrator.png';
import positioningBanner from '../../assets/herobanner.svg';
import assemblyShot from '../../assets/assembly.webp';
import composerShot from '../../assets/composer.webp';
import tracerShot from '../../assets/tracer.webp';

const pillars = [
  {
    title: 'AI Orchestrator',
    kicker: 'Conduct the journey',
    summary: 'Route use cases, tools, policies, and budgets.',
  },
  {
    title: 'AI Canvas',
    kicker: 'Shape the experience',
    summary: 'Embed a polished AI surface in your product.',
  },
  {
    title: 'AI Presence',
    kicker: 'Turn conversations into intent',
    summary: 'Classify intent, build memory, and surface insight.',
  },
];

const proofShots = [
  {
    title: 'Assembler',
    image: assemblyShot,
    alt: 'Assembly screenshot showing orchestrator workflow setup',
  },
  {
    title: 'Composer',
    image: composerShot,
    alt: 'Composer screenshot showing the AI journey experience',
  },
  {
    title: 'Tracer',
    image: tracerShot,
    alt: 'Tracer screenshot showing events and memory signals',
  },
];

const DEFAULT_RAIL_WIDTH = 440;
const MIN_RAIL_WIDTH = 360;
const MAX_RAIL_WIDTH = 440;
const RAIL_WIDTH_STORAGE_KEY = 'tagit-home-ai-rail-width';

function parseRailWidth(raw: string | null | undefined) {
  if (!raw) return DEFAULT_RAIL_WIDTH;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed)) return DEFAULT_RAIL_WIDTH;
  return Math.max(MIN_RAIL_WIDTH, Math.min(MAX_RAIL_WIDTH, parsed));
}

export default function DraftHomePage() {
  const [selectedProof, setSelectedProof] = useState<(typeof proofShots)[number] | null>(null);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [launchPrompt, setLaunchPrompt] = useState<{ id: number; text: string } | null>(null);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 960px)').matches;
  });
  const [isClientOpen, setIsClientOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    return false;
  });
  const [railWidth, setRailWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return DEFAULT_RAIL_WIDTH;
    return parseRailWidth(window.localStorage.getItem(RAIL_WIDTH_STORAGE_KEY));
  });

  useEffect(() => {
    const media = window.matchMedia('(max-width: 960px)');
    const update = () => {
      const nextMobile = media.matches;
      setIsMobile(nextMobile);
      if (nextMobile) {
        setIsClientOpen(false);
      }
    };

    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(RAIL_WIDTH_STORAGE_KEY, String(railWidth));
  }, [railWidth]);

  const railWidthCss = `${railWidth}px`;

  return (
    <div
      className={`app draft-home-shell ${!isMobile && isClientOpen ? 'is-rail-open' : 'is-rail-collapsed'}`}
      style={{ ['--draft-rail-width' as string]: railWidthCss }}
    >
      <div className="draft-home-content">
        <Header />

        <main className="draft-home-layout">
          <div className="draft-home-main">
            <section id="top" className="section section--surface-light draft-hero">
              <div className="container draft-hero-shell">
                <div className="draft-hero-copy">
                  <h1>
                    Build and run <span className="draft-hero-accent">AI journeys</span>, not just chat.
                  </h1>
                  <p className="lead draft-hero-lead">
                    Orchestrate the journey, shape the experience, and turn conversations into intent.
                    tagIt gives product teams the infrastructure to collect, route, and measure the AI layer.
                  </p>

                  <div className="hero-actions draft-hero-actions">
                    <button
                      type="button"
                      className="btn hero-btn-primary"
                      onClick={() => setIsContactOpen(true)}
                    >
                      Request access
                    </button>
                    <button
                      type="button"
                      className="btn hero-btn-secondary"
                      onClick={() => {
                        setIsContactOpen(false);
                        setIsClientOpen(true);
                        setLaunchPrompt({
                          id: Date.now(),
                          text: 'How does tagIt work?',
                        });
                      }}
                    >
                      See how it works
                    </button>
                  </div>
                </div>

                <div className="draft-hero-art" aria-label="tagIt platform preview">
                  <img
                    className="draft-hero-art-image"
                    src={heroBanner}
                    alt="tagIt platform illustration showing orchestration, experience, and intelligence panels"
                    loading="eager"
                  />
                </div>
              </div>
            </section>

            <section id="product" className="section section--surface-dark draft-positioning">
              <div className="container">
                <div className="section-head section-head--wide">
                  <h2>Three building blocks for governed AI</h2>
                  <p>Orchestrate the journey, shape the experience, and measure the result.</p>
                </div>

                <div className="draft-pillars">
                  {pillars.map((pillar) => (
                    <div key={pillar.title} className="draft-pillar card">
                      <div className="draft-pillar-kicker">{pillar.kicker}</div>
                      <h3 className="draft-pillar-title">{pillar.title}</h3>
                      <p className="draft-pillar-summary">{pillar.summary}</p>
                    </div>
                  ))}
                </div>

                <div className="draft-positioning-visual" aria-label="tagIt platform journey illustration">
                  <img
                    className="draft-positioning-visual-image"
                    src={positioningBanner}
                    alt="tagIt platform journey illustration"
                    loading="lazy"
                  />
                </div>
              </div>
            </section>

            <section id="proof" className="section section--surface-light draft-proof">
              <div className="draft-proof-shell">
                <div className="section-head section-head--wide">
                  <h2>Inside the platform</h2>
                  <p>A few real views from the product.</p>
                </div>

                <div className="draft-proof-grid">
                  {proofShots.map((shot) => (
                    <button
                      key={shot.title}
                      type="button"
                      className="draft-proof-card"
                      onClick={() => setSelectedProof(shot)}
                    >
                      <div className="draft-proof-label">{shot.title}</div>
                      <img className="draft-proof-image" src={shot.image} alt={shot.alt} loading="lazy" />
                    </button>
                  ))}
                </div>
              </div>
            </section>

            <section id="inquiry" className="section section--surface-dark section-cta">
              <div className="container">
                <div className="mini-cta">
                  <div>
                    <div className="mini-cta-title">Built to make AI systems transparent.</div>
                    <div className="mini-cta-body">
                      Orchestrate the journey, shape the experience, and turn every conversation into intent.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setIsContactOpen(true)}
                  >
                    Talk to us
                  </button>
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>

      <aside className="draft-home-rail">
        <TagItAiDemo
          presentation="sidePanel"
          mobilePresentation="fullscreen"
          mobileBreakpointPx={960}
          open={isClientOpen}
          onOpenChange={setIsClientOpen}
          panelWidth={isMobile ? '100vw' : railWidthCss}
          onPanelWidthChange={(nextWidth) => {
            if (!isMobile) {
              setRailWidth(parseRailWidth(nextWidth));
            }
          }}
          resizable={!isMobile}
          tabHeightPercent={isMobile ? 12 : 18}
          minWidthPx={isMobile ? 0 : 360}
          maxWidthPx={MAX_RAIL_WIDTH}
          launchPrompt={launchPrompt}
          onLaunchPromptConsumed={() => setLaunchPrompt(null)}
        />
      </aside>

      <ContactModal open={isContactOpen} onClose={() => setIsContactOpen(false)} />

      {selectedProof ? (
        <div
          className="draft-proof-modal"
          role="dialog"
          aria-modal="true"
          aria-label={`${selectedProof.title} preview`}
          onClick={() => setSelectedProof(null)}
        >
          <button
            type="button"
            className="draft-proof-modal-close"
            onClick={() => setSelectedProof(null)}
            aria-label="Close preview"
          >
            ×
          </button>
          <div className="draft-proof-modal-frame" onClick={(e) => e.stopPropagation()}>
            <img
              className="draft-proof-modal-image"
              src={selectedProof.image}
              alt={selectedProof.alt}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
